import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, effect, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	CheckboxComponent,
	DatepickerComponent,
	DividerComponent,
	FormGroupComponent,
	InfoGroupComponent,
	InputComponent,
	ModalComponent,
	SelectComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { endOfLocalToday } from '../../../core/utils/utils';
import { IIdentityDetailBody } from '../../../identities/models/identities.models';
import { IdentitiesService } from '../../../identities/services/identities.service';
import {
	applyExpirationPermanentState,
	buildPersonUpdatePayload,
	createPersonEditFormGroup,
	isNoExpirationDate,
} from '../../../identities/utils/person-edit.util';
import { IProcessUpdatePayload } from '../../../processes/models/processes.models';
import { MasterService } from '../../services/master.service';
import { ModalConfirmationService } from '../../services/modal-confirmation.service';
import { ReactiveFormsService } from '../../services/reactive-forms.service';

@Component({
	selector: 'app-modal-edit-identity',
	imports: [
		ReactiveFormsModule,
		ModalComponent,
		TranslatePipe,
		AlertComponent,
		TileComponent,
		FormGroupComponent,
		InputComponent,
		SelectComponent,
		DatepickerComponent,
		CheckboxComponent,
		DividerComponent,
		InfoGroupComponent,
	],
	templateUrl: './modal-edit-identity.component.html',
	styleUrl: './modal-edit-identity.component.scss',
})
export class ModalEditIdentityComponent implements OnInit {
	@Input({ required: true }) detail!: IIdentityDetailBody;
	/**
	 * process-detail: siempre manda process_uuid (historial).
	 * identities-detail: solo si cambia identifier (API lo exige entonces).
	 */
	@Input() alwaysAttachProcessUuid = false;
	@Output() closeEvent = new EventEmitter<boolean>();

	private identitiesService = inject(IdentitiesService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);
	private modalConfirmationService = inject(ModalConfirmationService);
	public masterService = inject(MasterService);
	public reactiveFormService = inject(ReactiveFormsService);

	public form: FormGroup = createPersonEditFormGroup();
	public readonly birthdateMaxDate = endOfLocalToday();
	public allCountries = this.masterService.countries;
	public errorMsg = '';
	public saving = false;
	public documentTypeLabel = '';
	private processUuid: string | undefined;
	private previousExpirationState = { previousExpirationDate: '' as unknown };

	constructor() {
		this.form
			.get('expiration_permanent')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((isPermanent: boolean) =>
				applyExpirationPermanentState(this.form, !!isPermanent, this.previousExpirationState),
			);

		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (!modalResponse) return;
			if (modalResponse.id === 'edit-identity-reassign') {
				if (modalResponse.action === 'buttonPrimary') {
					this.save({ overwriteExisting: true });
				}
				return;
			}
			if (modalResponse.id === 'edit-identity-underage') {
				if (modalResponse.action === 'buttonPrimary') {
					this.save({ confirmUnderageLegalAge: true });
				}
			}
		});
	}

	ngOnInit(): void {
		this.documentTypeLabel = this.masterService.getDocumentTypeLabel(this.detail?.document_type);
		// Solo para cambio de identifier (required en API). Edits normales no llevan process_uuid.
		this.processUuid = this.detail?.primary_process_uuid;
		this.patchFromPerson();
	}

	public closeModal(type: string): void {
		if (type === 'buttonPrimary') {
			this.save();
			return;
		}
		this.closeEvent.emit(false);
	}

	private patchFromPerson(): void {
		const expirationDate = this.detail?.expiration_date ?? '';
		const isPermanent = isNoExpirationDate(expirationDate);
		this.previousExpirationState.previousExpirationDate = '';
		this.form.patchValue(
			{
				identifier: this.detail?.identifier ?? '',
				nationality: this.detail?.nationality ?? '',
				expiration_date: expirationDate,
				expiration_permanent: isPermanent,
				birthdate: this.detail?.birth_date ?? '',
				gender: this.detail?.sex ?? '',
				names: this.detail?.names ?? '',
				surnames: this.detail?.surnames ?? '',
			},
			{ emitEvent: false },
		);
		applyExpirationPermanentState(this.form, isPermanent, this.previousExpirationState);
	}

	private save(options: { overwriteExisting?: boolean; confirmUnderageLegalAge?: boolean } = {}): void {
		this.errorMsg = '';
		if (this.saving) {
			return;
		}
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		const personUuid = this.detail?.uuid;
		if (!personUuid) {
			this.errorMsg = this.trans.instant('IDENTITIES.DETAIL.EDIT_SAVE_ERROR');
			return;
		}
		this.saving = true;
		const formRaw = this.form.getRawValue();
		const identifierChanged =
			String(formRaw.identifier ?? '').trim() !== String(this.detail?.identifier ?? '').trim();
		const attachProcessUuid = this.alwaysAttachProcessUuid || identifierChanged;
		// Solo campos Person; sin operation_uuid / operations_data (OCR no se toca).
		const body: IProcessUpdatePayload = buildPersonUpdatePayload({
			formRawValue: formRaw,
			processUuid: attachProcessUuid ? this.processUuid : undefined,
			overwriteExisting: options.overwriteExisting,
			confirmUnderageLegalAge: options.confirmUnderageLegalAge,
		});
		this.identitiesService.update(personUuid, body).subscribe({
			next: () => {
				this.saving = false;
				this.closeEvent.emit(true);
			},
			error: (error: HttpErrorResponse) => {
				this.saving = false;
				if (error.status === 409 && error.error?.code === 'IDENTIFIER_EXISTS') {
					this.modalConfirmationService.show({
						id: 'edit-identity-reassign',
						title: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_TITLE'),
						description: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_DESCRIPTION'),
						buttonPrimaryLabel: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_ACCEPT'),
						buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
						buttonPrimaryStyle: true,
					});
					return;
				}
				if (error.status === 409 && error.error?.code === 'LEGAL_AGE_BELOW_MINIMUM') {
					const yearsOld = Number(error.error?.data?.years);
					const legalAge = Number(error.error?.data?.legal_age);
					this.modalConfirmationService.show({
						id: 'edit-identity-underage',
						title: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_TITLE'),
						description: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_DESCRIPTION', {
							yearsOld: Number.isFinite(yearsOld) ? yearsOld : 0,
							legalAge: Number.isFinite(legalAge) ? legalAge : 0,
						}),
						buttonPrimaryLabel: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_ACCEPT'),
						buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
						buttonPrimaryStyle: true,
					});
					return;
				}
				this.errorMsg =
					error?.error?.message || this.trans.instant('IDENTITIES.DETAIL.EDIT_SAVE_ERROR');
			},
		});
	}
}
